import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Header,
  Pagination,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
  TextFilter,
} from '@cloudscape-design/components';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { usePageTitle } from '../lib/usePageTitle';
import { useNotifications } from '../notifications/useNotifications';
import { fmtDateTime } from '../format';
import {
  severityIndicatorType,
  useAckHealthEvent,
  useHealthEvents,
} from '../hooks/useHealthEvents';
import { collectorRunStatusIndicatorType, useCollectorRuns } from '../hooks/useCollectorRuns';
import type { CollectorRunDto, HealthEventDto, SourceKind } from '../api/contracts';

const SOURCE_KINDS: readonly SourceKind[] = [
  'emporia',
  'ecoflow_dpu',
  'ecoflow_shp2',
  'georgia_power',
];

const SOURCE_KIND_LABEL: Record<SourceKind, string> = {
  emporia: 'Emporia',
  ecoflow_dpu: 'EcoFlow DPU',
  ecoflow_shp2: 'EcoFlow SHP2',
  georgia_power: 'Georgia Power',
};

function HealthEventsTable() {
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const unacknowledged = !showAcknowledged;
  const { data, isLoading, isError, error } = useHealthEvents(unacknowledged);
  const { notifyError, notifySuccess } = useNotifications();
  const ackMutation = useAckHealthEvent();

  useEffect(() => {
    if (isError) notifyError(error, 'load health events');
  }, [isError, error, notifyError]);

  const events = data?.events ?? [];

  const { items, collectionProps, filterProps, paginationProps } = useCollection(events, {
    filtering: {
      empty: (
        <Box textAlign="center" color="inherit">
          No health events found.
        </Box>
      ),
      noMatch: (
        <Box textAlign="center" color="inherit">
          No matching health events.
        </Box>
      ),
    },
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: 'ts' },
        isDescending: true,
      },
    },
    pagination: { pageSize: 20 },
  });

  function handleAck(event: HealthEventDto) {
    ackMutation.mutate(
      { ts: event.ts, id: event.id },
      {
        onSuccess: () => notifySuccess('Health event acknowledged', event.identity),
        onError: (err) => notifyError(err, 'acknowledge health event'),
      },
    );
  }

  return (
    <Table
      {...collectionProps}
      loading={isLoading}
      loadingText="Loading health events…"
      items={items}
      trackBy="id"
      variant="container"
      header={
        <Header
          variant="h2"
          counter={`(${events.length})`}
          actions={
            <SegmentedControl
              selectedId={showAcknowledged ? 'all' : 'unacknowledged'}
              onChange={({ detail }) => setShowAcknowledged(detail.selectedId === 'all')}
              options={[
                { id: 'unacknowledged', text: 'Unacknowledged' },
                { id: 'all', text: 'Show all' },
              ]}
            />
          }
        >
          Data quality alerts
        </Header>
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Find health events"
          countText={`${items.length} match${items.length === 1 ? '' : 'es'}`}
        />
      }
      pagination={<Pagination {...paginationProps} />}
      columnDefinitions={[
        {
          id: 'severity',
          header: 'Severity',
          cell: (item) => (
            <StatusIndicator type={severityIndicatorType(item.severity)}>
              {item.severity}
            </StatusIndicator>
          ),
          sortingField: 'severity',
        },
        {
          id: 'identity',
          header: 'Identity',
          cell: (item) => item.identity,
          sortingField: 'identity',
        },
        {
          id: 'ts',
          header: 'Timestamp',
          cell: (item) => fmtDateTime(item.ts),
          sortingField: 'ts',
        },
        {
          id: 'channel',
          header: 'Channel',
          // Raw channelId only -- see DataQuality.tsx report note on why this
          // page does not join against /api/topology for a display name.
          cell: (item) => item.channelId ?? '—',
        },
        {
          id: 'acknowledgedAt',
          header: 'Acknowledged',
          cell: (item) => (item.acknowledgedAt ? fmtDateTime(item.acknowledgedAt) : '—'),
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: (item) =>
            item.acknowledgedAt ? (
              <StatusIndicator type="success">Acknowledged</StatusIndicator>
            ) : (
              <Button
                onClick={() => handleAck(item)}
                loading={ackMutation.isPending && ackMutation.variables?.id === item.id}
              >
                Acknowledge
              </Button>
            ),
        },
      ]}
    />
  );
}

function CollectorRunsTable({ sourceKind }: { sourceKind: SourceKind }) {
  const { data, isLoading, isError, error } = useCollectorRuns(sourceKind, 20);
  const { notifyError } = useNotifications();

  useEffect(() => {
    if (isError) notifyError(error, `load ${SOURCE_KIND_LABEL[sourceKind]} collector runs`);
  }, [isError, error, notifyError, sourceKind]);

  const runs = data?.runs ?? [];

  const { items, collectionProps, paginationProps } = useCollection(runs, {
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: 'startedAt' },
        isDescending: true,
      },
    },
    pagination: { pageSize: 10 },
  });

  return (
    <Table
      {...collectionProps}
      loading={isLoading}
      loadingText="Loading collector runs…"
      items={items}
      trackBy="id"
      variant="embedded"
      pagination={items.length > 10 ? <Pagination {...paginationProps} /> : undefined}
      empty={
        <Box textAlign="center" color="inherit">
          No collector runs recorded yet.
        </Box>
      }
      columnDefinitions={[
        {
          id: 'status',
          header: 'Status',
          cell: (item: CollectorRunDto) => (
            <StatusIndicator type={collectorRunStatusIndicatorType(item.status)}>
              {item.status}
            </StatusIndicator>
          ),
          sortingField: 'status',
        },
        {
          id: 'windowStart',
          header: 'Window start',
          cell: (item: CollectorRunDto) => fmtDateTime(item.windowStart),
          sortingField: 'windowStart',
        },
        {
          id: 'windowEnd',
          header: 'Window end',
          cell: (item: CollectorRunDto) => fmtDateTime(item.windowEnd),
          sortingField: 'windowEnd',
        },
        {
          id: 'rowsWritten',
          header: 'Rows written',
          cell: (item: CollectorRunDto) => item.rowsWritten,
          sortingField: 'rowsWritten',
        },
        {
          id: 'error',
          header: 'Error',
          cell: (item: CollectorRunDto) => item.error ?? '—',
        },
        {
          id: 'startedAt',
          header: 'Started',
          cell: (item: CollectorRunDto) => fmtDateTime(item.startedAt),
          sortingField: 'startedAt',
        },
        {
          id: 'finishedAt',
          header: 'Finished',
          cell: (item: CollectorRunDto) => (item.finishedAt ? fmtDateTime(item.finishedAt) : '—'),
        },
      ]}
    />
  );
}

export default function DataQuality() {
  usePageTitle('Data Quality');

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Data Quality</Header>
      <HealthEventsTable />
      <Tabs
        tabs={SOURCE_KINDS.map((kind) => ({
          id: kind,
          label: SOURCE_KIND_LABEL[kind],
          content: <CollectorRunsTable sourceKind={kind} />,
        }))}
      />
    </SpaceBetween>
  );
}
